import { MessageDescriptor } from 'react-intl';
import { matchPath } from 'react-router-dom';
import { EAuthRoutes, FSEAuthRoutes, IUserData } from '@agco-onedigital/ecosystem-lib-frontend-utils';
import { commonMessages } from 'messages';
import { EFleetDetailsTabs } from '../../pages/FleetDetails/types';

export interface IFeatureMapping {
  name: string;
  route: string;
  alertName: string; // Feature name with -alert postfix for tracking modal display
  displayName: MessageDescriptor;
}

const EXPERTLINK_ALERT_NAME = 'expertlink-alert';
const EXPERTLINK_DISPLAY_NAME = commonMessages.dashboard.expertLinkTitle;

export const FEATURE_MAPPINGS: Record<string, IFeatureMapping> = {
  VIDEOS: {
    name: 'videos',
    route: FSEAuthRoutes.VIDEOS,
    alertName: 'videos-alert',
    displayName: commonMessages.menu.videos,
  },
  DTC: {
    name: 'dtc',
    route: FSEAuthRoutes.DIAGNOSTIC_TROUBLE_CODES,
    alertName: 'dtc-alert',
    displayName: commonMessages.menu.diagnosticTroubleCodes,
  },
  MARKETING_PREFERENCES: {
    name: 'marketing-preferences',
    route: `${EAuthRoutes.USER}${EAuthRoutes.MARKETING_PREFERENCES}`,
    alertName: 'marketing-preferences-alert',
    displayName: commonMessages.registration.consentFinish.marketing,
  },
  EXPERTLINK_YSA_TAB: {
    name: 'expertlink-ysa-tab',
    route: `${FSEAuthRoutes.SUPPORT_ACTIVITIES}/expertlink-tickets`,
    alertName: EXPERTLINK_ALERT_NAME,
    displayName: EXPERTLINK_DISPLAY_NAME,
  },
  EXPERTLINK_SIDEBAR: {
    name: 'expertlink-sidebar',
    route: FSEAuthRoutes.SUPPORT_ACTIVITIES,
    alertName: EXPERTLINK_ALERT_NAME,
    displayName: EXPERTLINK_DISPLAY_NAME,
  },
  EXPERTLINK_MACHINE_TAB: {
    name: 'expertlink-machine-tab',
    route: `${EAuthRoutes.FLEET_OVERVIEW}/:id/${EFleetDetailsTabs.EXPERTLINK}`,
    alertName: EXPERTLINK_ALERT_NAME,
    displayName: EXPERTLINK_DISPLAY_NAME,
  },
  EXPERTLINK_CONTACT_US: {
    name: 'expertlink-contact-us',
    route: `${FSEAuthRoutes.CONTACT_US}/open-expertlink-ticket`,
    alertName: EXPERTLINK_ALERT_NAME,
    displayName: EXPERTLINK_DISPLAY_NAME,
  },
  // Add more features here as needed
  // MANUALS: {
  //   name: 'manuals',
  //   route: FSEAuthRoutes.MANUALS,
  //   alertName: 'manuals-alert',
  //   displayName: commonMessages.menu.manuals,
  // },
};

export const EXPERTLINK_FEATURES: IFeatureMapping[] = [
  FEATURE_MAPPINGS.EXPERTLINK_YSA_TAB,
  FEATURE_MAPPINGS.EXPERTLINK_SIDEBAR,
  FEATURE_MAPPINGS.EXPERTLINK_MACHINE_TAB,
  FEATURE_MAPPINGS.EXPERTLINK_CONTACT_US,
];

export const getFeatureByRoute = (pathname: string): IFeatureMapping | undefined => {
  return Object.values(FEATURE_MAPPINGS).find(feature =>
    matchPath({ path: feature.route, caseSensitive: true, end: false }, pathname),
  );
};

export const getFeatureByName = (name: string): IFeatureMapping | undefined => {
  return Object.values(FEATURE_MAPPINGS).find(feature => feature.name === name);
};

export const hasUserVisitedFeature = (featureName: string, userFeatures: string[] = []): boolean => {
  return userFeatures.includes(featureName);
};

export const hasUserSeenAlert = (alertName: string, userFeatures: string[] = []): boolean => {
  return userFeatures.includes(alertName);
};

export const hasUserChangedBeyondFeatures = (previousUser: IUserData | undefined, user: IUserData): boolean =>
  !previousUser ||
  Object.keys({ ...previousUser, ...user }).some(
    key => key !== 'features' && previousUser[key as keyof IUserData] !== user[key as keyof IUserData],
  );
